import { EntryEditError, imageTypeOf } from '../edits.ts';
import { javaIsBlank } from '../javaText.ts';
import { int, scalar, text, transaction, type SqlConnection } from '../sql.ts';
import { decodePoints, type MapPoint } from './geometry.ts';
import type { MapIcon } from './icons.ts';

/**
 * The Atlas's maps (docs/PARITY-STEP-4-PLAN.md section 4), read and written as the desktop's
 * `AtlasRepository` and `AtlasService` do:
 * - maps by name, without regard to case, each with its pin and region counts and its
 *   gallery thumbnail (a PNG at most 320 by 200, which the page makes);
 * - a map's pins and regions by when they were made; categories by name;
 * - a map image is PNG, JPEG or WebP, up to 50 MB, kept exactly as it was imported;
 * - a map's parents are the maps with a pin or region that opens it, and its breadcrumb
 *   follows the first by name up to the top, stopping at a loop;
 * - deleting a map deletes its pins, regions and drawings; pins, regions and cards that
 *   opened it are left unlinked (the schema's cascades).
 */

export const ATLAS_LIMITS = {
  name: 200,
  /** `AtlasService.MAX_IMAGE_BYTES`. */
  imageBytes: 50 * 1024 * 1024,
  /** `THUMBNAIL_WIDTH` and `THUMBNAIL_HEIGHT`. */
  thumbnailWidth: 320,
  thumbnailHeight: 200,
} as const;

/** The map image types the desktop names (`ImageDecoding.guessMimeType`). */
export const MAP_IMAGE_TYPES: ReadonlySet<string> = new Set(['image/png', 'image/jpeg', 'image/webp']);

export type GridType = 'NONE' | 'SQUARE' | 'HEX';
export type MarkerStyle = 'PIN' | 'TOKEN';

export interface MapSummary {
  readonly id: string;
  readonly name: string;
  readonly mimeType: string;
  readonly markerCount: number;
  readonly regionCount: number;
  readonly thumbnail: Uint8Array | null;
  readonly created: string;
  readonly modified: string;
}

export interface MapScale {
  /** 0 when the map has no scale. */
  readonly unitsPerPixel: number;
  readonly unit: string;
}

export interface MapGrid {
  readonly type: GridType;
  /** In image pixels; for a hex grid, between opposite flat sides. */
  readonly cellSize: number;
  readonly colorHex: string;
  readonly opacity: number;
}

export interface MapFogStored {
  readonly enabled: boolean;
  /** The revealed area: rings of "x,y;x,y;..." joined by "|", read even-odd (`MapFog.encode`). */
  readonly revealed: string;
}

export interface AtlasMap {
  readonly id: string;
  readonly name: string;
  readonly mimeType: string;
  readonly imageData: Uint8Array;
  readonly scale: MapScale;
  readonly grid: MapGrid;
  readonly fog: MapFogStored;
  readonly created: string;
  readonly modified: string;
}

export interface MarkerCategory {
  readonly id: string;
  readonly name: string;
  readonly colorHex: string;
  readonly icon: string;
  readonly created: string;
}

export interface MapMarker {
  readonly id: string;
  readonly mapId: string;
  readonly x: number;
  readonly y: number;
  readonly label: string;
  readonly categoryId: string | null;
  readonly notes: string;
  /** False for "Hidden from players (GM only)". */
  readonly visible: boolean;
  readonly linkedEntryId: string | null;
  readonly linkedMapId: string | null;
  readonly icon: string;
  readonly style: MarkerStyle;
  readonly created: string;
  readonly modified: string;
}

export interface MapRegion {
  readonly id: string;
  readonly mapId: string;
  readonly label: string;
  readonly points: readonly MapPoint[];
  readonly categoryId: string | null;
  /** Its own colour; null to use its category's. */
  readonly colorHex: string | null;
  readonly notes: string;
  readonly visible: boolean;
  readonly linkedEntryId: string | null;
  readonly linkedMapId: string | null;
  readonly created: string;
  readonly modified: string;
}

/** `MapRegion.MIN_POINTS`. */
export const REGION_MIN_POINTS = 3;
/** The colour of a pin or region with no category (`MapExport.NEUTRAL`). */
export const NEUTRAL_COLOR = '#6B6470';

// ---- reading ----

export function listMaps(conn: SqlConnection): MapSummary[] {
  return conn
    .all(
      `SELECT m.id, m.name, m.mime_type, m.created, m.modified, m.thumbnail_data,
         (SELECT COUNT(*) FROM map_markers mk WHERE mk.map_id = m.id) AS marker_count,
         (SELECT COUNT(*) FROM map_regions r WHERE r.map_id = m.id) AS region_count
       FROM atlas_maps m ORDER BY m.name COLLATE NOCASE`,
    )
    .map((r) => ({
      id: text(r['id']),
      name: mapName(r['name']),
      mimeType: text(r['mime_type']),
      markerCount: int(r['marker_count']),
      regionCount: int(r['region_count']),
      thumbnail: r['thumbnail_data'] instanceof Uint8Array ? r['thumbnail_data'] : null,
      created: text(r['created']),
      modified: text(r['modified']),
    }));
}

export function getMap(conn: SqlConnection, id: string): AtlasMap | null {
  const r = conn.all(
    `SELECT id, name, mime_type, image_data, created, modified, scale_units_per_pixel, scale_unit,
       grid_type, grid_size, grid_color, grid_opacity, fog_enabled, fog_revealed
     FROM atlas_maps WHERE id = ?`,
    [id],
  )[0];
  if (!r) return null;
  const mime = text(r['mime_type']);
  return {
    id: text(r['id']),
    name: mapName(r['name']),
    mimeType: javaIsBlank(mime) ? 'application/octet-stream' : mime,
    imageData: r['image_data'] instanceof Uint8Array ? r['image_data'] : new Uint8Array(),
    scale: scaleOf(num(r['scale_units_per_pixel']), text(r['scale_unit'])),
    grid: gridOf(text(r['grid_type']), num(r['grid_size']), text(r['grid_color']), num(r['grid_opacity'])),
    fog: { enabled: int(r['fog_enabled']) !== 0, revealed: text(r['fog_revealed']) },
    created: text(r['created']),
    modified: text(r['modified']),
  };
}

export function listCategories(conn: SqlConnection): MarkerCategory[] {
  return conn.all('SELECT id, name, color, icon, created FROM marker_categories ORDER BY name COLLATE NOCASE').map((r) => {
    const name = text(r['name']);
    const color = text(r['color']);
    return { id: text(r['id']), name: javaIsBlank(name) ? 'Category' : name, colorHex: javaIsBlank(color) ? NEUTRAL_COLOR : color, icon: text(r['icon']).trim(), created: text(r['created']) };
  });
}

export function listMapMarkers(conn: SqlConnection, mapId: string): MapMarker[] {
  return conn
    .all('SELECT id, map_id, x, y, label, category_id, notes, visible, linked_entry_id, linked_map_id, icon, style, created, modified FROM map_markers WHERE map_id = ? ORDER BY created', [mapId])
    .map((r) => ({
      id: text(r['id']),
      mapId: text(r['map_id']),
      x: num(r['x']),
      y: num(r['y']),
      label: text(r['label']),
      categoryId: optional(r['category_id']),
      notes: text(r['notes']),
      visible: int(r['visible']) !== 0,
      linkedEntryId: optional(r['linked_entry_id']),
      linkedMapId: optional(r['linked_map_id']),
      icon: text(r['icon']).trim(),
      style: text(r['style']).toUpperCase() === 'TOKEN' ? 'TOKEN' : 'PIN',
      created: text(r['created']),
      modified: text(r['modified']),
    }));
}

export function listMapRegions(conn: SqlConnection, mapId: string): MapRegion[] {
  return conn
    .all('SELECT id, map_id, label, points, category_id, color, notes, visible, linked_entry_id, linked_map_id, created, modified FROM map_regions WHERE map_id = ? ORDER BY created', [mapId])
    .map((r) => {
      const color = r['color'] === null || r['color'] === undefined ? '' : text(r['color']);
      return {
        id: text(r['id']),
        mapId: text(r['map_id']),
        label: text(r['label']),
        points: decodePoints(text(r['points'])),
        categoryId: optional(r['category_id']),
        colorHex: javaIsBlank(color) ? null : color,
        notes: text(r['notes']),
        visible: int(r['visible']) !== 0,
        linkedEntryId: optional(r['linked_entry_id']),
        linkedMapId: optional(r['linked_map_id']),
        created: text(r['created']),
        modified: text(r['modified']),
      };
    });
}

/** The icons imported into the realm, by pack and then name, without regard to case. */
export function listIcons(conn: SqlConnection): MapIcon[] {
  return conn.all('SELECT id, pack, name, kind, mime_type, data, created FROM map_icons ORDER BY pack COLLATE NOCASE, name COLLATE NOCASE').map((r) => {
    const pack = text(r['pack']);
    const name = text(r['name']);
    return {
      id: text(r['id']),
      pack: javaIsBlank(pack) ? 'Imported' : pack.trim(),
      name: javaIsBlank(name) ? 'Icon' : name.trim(),
      kind: text(r['kind']) === 'GLYPH' ? 'GLYPH' : 'PICTURE',
      mimeType: text(r['mime_type']),
      data: r['data'] instanceof Uint8Array ? r['data'] : new Uint8Array(),
      created: text(r['created']),
    };
  });
}

/** The colour a region is drawn in: its own, else its category's, else neutral (`effectiveColorHex`). */
export function regionColor(region: MapRegion, category: MarkerCategory | undefined): string {
  return region.colorHex ?? category?.colorHex ?? NEUTRAL_COLOR;
}

/**
 * From a top-level map down to this one (`AtlasService.breadcrumbs`), following the maps
 * with a pin or region that opens the next; the first by name where there are several.
 * Ends with the map itself.
 */
export function breadcrumbs(conn: SqlConnection, mapId: string): { readonly id: string; readonly name: string }[] {
  const names = new Map(conn.all('SELECT id, name FROM atlas_maps').map((r) => [text(r['id']), mapName(r['name'])]));
  const trail: { id: string; name: string }[] = [];
  const seen = new Set<string>();
  let current: string | undefined = mapId;
  while (current !== undefined && !seen.has(current) && names.has(current)) {
    seen.add(current);
    trail.unshift({ id: current, name: names.get(current)! });
    const parents: string[] = conn
      .all(
        `SELECT m.id FROM atlas_maps m
         WHERE m.id <> ? AND (EXISTS (SELECT 1 FROM map_markers mk WHERE mk.map_id = m.id AND mk.linked_map_id = ?)
           OR EXISTS (SELECT 1 FROM map_regions r WHERE r.map_id = m.id AND r.linked_map_id = ?))
         ORDER BY m.name COLLATE NOCASE`,
        [current, current, current],
      )
      .map((r) => text(r['id']));
    current = parents.find((p) => !seen.has(p));
  }
  return trail;
}

// ---- writing ----

/**
 * Imports a map (`AtlasService.importMap`): the picture's bytes as they are, typed by their
 * first bytes, with the thumbnail the page made of it. A blank name is "Untitled Map".
 */
export function importMap(
  conn: SqlConnection,
  name: string,
  imageData: Uint8Array,
  thumbnail: Uint8Array | null,
  id: string = crypto.randomUUID(),
  now: string = new Date().toISOString(),
  /** What a new map starts with (Settings > New things start with): no grid and no unit unless chosen. */
  defaults: { readonly gridType: GridType; readonly unit: string } = { gridType: 'NONE', unit: '' },
): string {
  if (imageData.byteLength === 0) throw new EntryEditError('invalid', 'The chosen file is empty.');
  if (imageData.byteLength > ATLAS_LIMITS.imageBytes) throw new EntryEditError('invalid', 'A map image can be at most 50 MB.');
  const type = imageTypeOf(imageData);
  if (type === null || !MAP_IMAGE_TYPES.has(type)) throw new EntryEditError('invalid', "That isn't a map image Realmery can use (PNG, JPEG or WebP).");
  checkThumbnail(thumbnail);
  conn.run(
    `INSERT INTO atlas_maps (id, name, mime_type, image_data, created, modified, thumbnail_data,
       scale_units_per_pixel, scale_unit, grid_type, grid_size, grid_color, grid_opacity, fog_enabled, fog_revealed)
     VALUES (?, ?, ?, ?, ?, ?, ?, 0, ?, ?, 50, '#000000', 0.3, 0, '')`,
    [id, cleanName(name), type, imageData, now, now, thumbnail, defaults.unit.trim().slice(0, 40), ['SQUARE', 'HEX'].includes(defaults.gridType) ? defaults.gridType : 'NONE'],
  );
  return id;
}

export function renameMap(conn: SqlConnection, id: string, name: string, now: string = new Date().toISOString()): void {
  const clean = cleanName(name);
  transaction(conn, () => {
    mustExist(conn, 'atlas_maps', id, 'That map no longer exists.');
    conn.run('UPDATE atlas_maps SET name = ?, modified = ? WHERE id = ?', [clean, now, id]);
  });
}

/** Keeps a thumbnail made for a map imported before thumbnails existed (`listMapSummaries`), without touching `modified`. */
export function storeThumbnail(conn: SqlConnection, id: string, thumbnail: Uint8Array): void {
  checkThumbnail(thumbnail);
  conn.run('UPDATE atlas_maps SET thumbnail_data = ? WHERE id = ?', [thumbnail, id]);
}

/** What deleting a map takes with it, and what it leaves unlinked. */
export interface MapDeletionImpact {
  readonly pins: number;
  readonly regions: number;
  readonly drawings: number;
  /** Pins and regions on other maps that open this one. */
  readonly openedFrom: number;
  /** Idea Board cards linked to it. */
  readonly cards: number;
  /** Chronology events linked to its pins. */
  readonly events: number;
}

export function mapDeletionImpact(conn: SqlConnection, id: string): MapDeletionImpact {
  const count = (sql: string, params: string[]) => int(scalar(conn, sql, params));
  return {
    pins: count('SELECT COUNT(*) FROM map_markers WHERE map_id = ?', [id]),
    regions: count('SELECT COUNT(*) FROM map_regions WHERE map_id = ?', [id]),
    drawings: count('SELECT COUNT(*) FROM map_annotations a JOIN map_layers l ON l.id = a.layer_id WHERE l.map_id = ?', [id]),
    openedFrom:
      count('SELECT COUNT(*) FROM map_markers WHERE linked_map_id = ? AND map_id <> ?', [id, id]) +
      count('SELECT COUNT(*) FROM map_regions WHERE linked_map_id = ? AND map_id <> ?', [id, id]),
    cards: count('SELECT COUNT(*) FROM board_objects WHERE linked_map_id = ?', [id]),
    events: count('SELECT COUNT(DISTINCT e.event_id) FROM chronology_event_markers e JOIN map_markers m ON m.id = e.marker_id WHERE m.map_id = ?', [id]),
  };
}

export function deleteMap(conn: SqlConnection, id: string): MapDeletionImpact {
  return transaction(conn, () => {
    mustExist(conn, 'atlas_maps', id, 'That map no longer exists.');
    const impact = mapDeletionImpact(conn, id);
    conn.run('DELETE FROM atlas_maps WHERE id = ?', [id]);
    return impact;
  });
}

// ---- helpers ----

const optional = (v: unknown) => (v === null || v === undefined ? null : String(v));
const num = (v: unknown) => (typeof v === 'number' ? v : typeof v === 'bigint' ? Number(v) : Number(v ?? 0));
const mapName = (v: unknown) => {
  const name = v === null || v === undefined ? '' : String(v);
  return javaIsBlank(name) ? 'Untitled Map' : name;
};

/** `MapScale`'s normalising: an unusable scale is none. */
function scaleOf(unitsPerPixel: number, unit: string): MapScale {
  return { unitsPerPixel: Number.isFinite(unitsPerPixel) && unitsPerPixel > 0 ? unitsPerPixel : 0, unit: unit.trim() };
}

/** `MapGrid`'s normalising. */
function gridOf(type: string, cellSize: number, colorHex: string, opacity: number): MapGrid {
  const upper = type.toUpperCase();
  return {
    type: upper === 'SQUARE' || upper === 'HEX' ? upper : 'NONE',
    cellSize: Number.isFinite(cellSize) ? Math.max(4, cellSize) : 50,
    colorHex: javaIsBlank(colorHex) ? '#000000' : colorHex,
    opacity: Number.isFinite(opacity) ? Math.max(0, Math.min(1, opacity)) : 0.3,
  };
}

function cleanName(name: string): string {
  if (name.length > ATLAS_LIMITS.name) throw new EntryEditError('invalid', `A name can be at most ${ATLAS_LIMITS.name} characters.`);
  return javaIsBlank(name) ? 'Untitled Map' : name.replace(/[\r\n]+/g, ' ');
}

function checkThumbnail(thumbnail: Uint8Array | null): void {
  if (thumbnail !== null && (imageTypeOf(thumbnail) !== 'image/png' || thumbnail.byteLength > 2 * 1024 * 1024)) {
    throw new EntryEditError('invalid', 'That thumbnail is not a small PNG.');
  }
}

/** `table` is always one of this module's literals, never input. */
function mustExist(conn: SqlConnection, table: string, id: string, message: string): void {
  if (int(scalar(conn, `SELECT COUNT(*) FROM ${table} WHERE id = ?`, [id])) === 0) throw new EntryEditError('not_found', message);
}

